/**
 * Felles feiltype og fetch-hjelpere for API-kall.
 *
 * `ApiError` bodde i features/admin/useMembers.ts; den er flyttet hit i #141
 * fordi innloggingen og de uautentiserte lenkerutene (/bli-med, /nytt-passord)
 * trenger den uten å avhenge av adminmodulen. useMembers re-eksporterer den,
 * så eksisterende importer virker uendret.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    /** Underkode, f.eks. `too_short` for `weak_password` (#141). */
    public readonly reason?: string,
    /** Sekunder fra `Retry-After`, når serveren sendte den (#141). */
    public readonly retryAfter?: number,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

export async function toApiError(res: Response): Promise<ApiError> {
  let code = 'unknown';
  let reason: string | undefined;
  try {
    const body = (await res.json()) as { error?: unknown; reason?: unknown };
    if (typeof body.error === 'string') code = body.error;
    if (typeof body.reason === 'string') reason = body.reason;
  } catch {
    /* non-JSON body */
  }
  const retryAfter = Number(res.headers.get('Retry-After') ?? '');
  return new ApiError(
    res.status,
    code,
    reason,
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  );
}

/** Alt som kastes fra et fetch-kall blir en `ApiError` — nettverksfeil får koden `network`. */
export function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'network');
}

/** fetch med cookie; kaster `ApiError` ved ikke-2xx og returnerer JSON-kroppen. */
export async function sendJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

/** RequestInit for en JSON-kropp. */
export function jsonInit(
  method: string,
  body: unknown,
  headers: Record<string, string> = {},
): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(body),
  };
}
